import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import EditableAttribute from 'pages/Client/EditableAttribute';
import type { FC } from 'react';
import type { FollowUpConfigDraft } from 'services/types/followUpConfig';

import ClampedNumberField from './ClampedNumberField';

interface SendingConfigurationSectionProps {
  followUpConfigDraft: FollowUpConfigDraft;
  isEditing: boolean;
  setFollowUpConfigDraft?: (followUpConfigDraft: FollowUpConfigDraft) => void;
}

const SendingConfigurationSection: FC<SendingConfigurationSectionProps> = ({
  followUpConfigDraft,
  isEditing,
  setFollowUpConfigDraft,
}) => {
  // Check if there's no template and maxMinutesWaitTime > 24 hours
  const hasNoTemplate = followUpConfigDraft.templateName === null;
  const exceeds24Hours = followUpConfigDraft.maxMinutesWaitTime > 24 * 60;
  const showWarning = hasNoTemplate && exceeds24Hours;

  return (
    <Card sx={{ mb: 1.5, boxShadow: 0, borderRadius: 0 }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 1.5, fontWeight: 600 }}>
          Configuración de Envío
        </Typography>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Tiempo mínimo de espera (minutos)"
                value={followUpConfigDraft.minMinutesWaitTime}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, minMinutesWaitTime: next })}
                min={1}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Tiempo mínimo de espera (minutos)
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.minMinutesWaitTime}</Typography>
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Tiempo máximo de espera (minutos)"
                value={followUpConfigDraft.maxMinutesWaitTime}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, maxMinutesWaitTime: next })}
                min={11}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Tiempo máximo de espera (minutos)
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.maxMinutesWaitTime}</Typography>
              </Box>
            )}
          </Grid>
          {showWarning && (
            <Grid size={12}>
              <Alert severity="warning" sx={{ mt: 1 }}>
                El tiempo máximo de espera no puede ser mayor a 24 horas cuando no hay un template asociado. Por favor,
                reduzca el tiempo máximo de espera o seleccione un template.
              </Alert>
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Hora mínima (ej: 9 para las 9:00)"
                value={followUpConfigDraft.minWorkingHour}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, minWorkingHour: next })}
                min={0}
                max={23}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Hora mínima (ej: 9 para las 9:00)
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.minWorkingHour}</Typography>
              </Box>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            {isEditing ? (
              <ClampedNumberField
                label="Hora máxima (ej: 18 para las 18:00)"
                value={followUpConfigDraft.maxWorkingHour}
                onChange={(next) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, maxWorkingHour: next })}
                min={0}
                max={23}
                fullWidth
              />
            ) : (
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 500,
                    display: 'block',
                    mb: 0.5,
                  }}
                >
                  Hora máxima (ej: 18 para las 18:00)
                </Typography>
                <Typography variant="body2">{followUpConfigDraft.maxWorkingHour}</Typography>
              </Box>
            )}
          </Grid>
          <Grid size={12}>
            <EditableAttribute
              label="Enviar fin de semana"
              value={followUpConfigDraft.sendOnWeekend}
              onChange={(e) => setFollowUpConfigDraft?.({ ...followUpConfigDraft, sendOnWeekend: e.target.checked })}
              disabled={!isEditing}
              type="checkbox"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SendingConfigurationSection;
